import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Droplets } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { Alert } from '../atoms/Alert/Alert';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { EmptyState } from '../atoms/EmptyState';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { formatNumber, formatSigned } from '../dosing/khSupplyFormat';
import { getKhUsage, khUsageQueryKey, type KhUsage } from '../../services/khSupplyService';
import './KhUsageCard.css';

interface KhUsageCardProps {
  aquariumId?: string;
}

/**
 * Dashboard card "Current KH usage" (KH/Ca upkeep module): what the configured
 * method adds per day, the KH drift of the recent tests and the resulting usage.
 */
export function KhUsageCard({ aquariumId }: KhUsageCardProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const usageQuery = useQuery({
    queryKey: khUsageQueryKey(aquariumId ?? ''),
    queryFn: () => getKhUsage(aquariumId ?? ''),
    enabled: aquariumId != null && aquariumId.length > 0,
  });
  const dosingPath = aquariumId ? `/aquariums/${aquariumId}/dosing` : null;

  const header = (
    <div className="kh-usage-card__header">
      <Heading level={3} className="kh-usage-card__title">{t('dashboard.khUsage.title')}</Heading>
      {dosingPath && usageQuery.data?.configured && (
        <Link to={dosingPath} className="kh-usage-card__link">{t('dashboard.khUsage.manage')}</Link>
      )}
    </div>
  );

  if (aquariumId == null || usageQuery.isLoading) {
    return (
      <div className="kh-usage-card" data-testid="kh-usage-card" aria-busy="true">
        {header}
        <LiveRegion visuallyHidden busy>{t('dashboard.khUsage.loading')}</LiveRegion>
        <div className="kh-usage-card__skeleton" aria-hidden="true" />
      </div>
    );
  }

  if (usageQuery.isError || usageQuery.data == null) {
    return (
      <div className="kh-usage-card" data-testid="kh-usage-card">
        {header}
        <LoadErrorState compact title={t('dashboard.khUsage.loadError')} live="polite" onRetry={() => void usageQuery.refetch()} />
      </div>
    );
  }

  const usage = usageQuery.data;
  if (!usage.configured) {
    return (
      <div className="kh-usage-card" data-testid="kh-usage-card">
        {header}
        <EmptyState
          headingLevel={4}
          icon={<Droplets size={32} />}
          headline={t('dashboard.khUsage.emptyTitle')}
          description={t('dashboard.khUsage.emptyDescription')}
          actionLabel={t('dashboard.khUsage.emptyAction')}
          onAction={() => dosingPath && navigate(dosingPath)}
          actionVariant="secondary"
        />
      </div>
    );
  }

  return (
    <div className="kh-usage-card" data-testid="kh-usage-card">
      {header}
      <KhUsageDetails usage={usage} language={i18n.language} t={t} />
    </div>
  );
}

type TFunction = (key: string, options?: Record<string, unknown>) => string;

function KhUsageDetails({ usage, language, t }: { usage: KhUsage; language: string; t: TFunction }) {
  const n = (value: number, digits = 2) => formatNumber(value, language, digits);
  const method = usage.method ? t(`dosing.khSupply.methods.${usage.method}`) : '';

  if (usage.volume_liters === null) {
    return (
      <Alert tone="info" compact live="off" data-testid="kh-usage-no-volume">
        {t('dashboard.khUsage.noVolume')}
      </Alert>
    );
  }
  if (usage.usage_dkh_per_day === null || usage.supply_dkh_per_day === null) {
    return (
      <Alert tone="info" compact live="off">
        {t('dashboard.khUsage.incomplete')}
      </Alert>
    );
  }

  const rows: Array<{ term: string; value: string }> = [
    { term: t('dashboard.khUsage.method'), value: method },
    { term: t('dashboard.khUsage.supply'), value: `${n(usage.supply_dkh_per_day)} dKH/${t('dashboard.khUsage.day')}` },
    {
      term: t('dashboard.khUsage.trend'),
      value: usage.kh_trend_dkh_per_day === null
        ? t('dashboard.khUsage.trendNone')
        : `${formatSigned(usage.kh_trend_dkh_per_day, language)} dKH/${t('dashboard.khUsage.day')}`,
    },
  ];
  if (usage.supply_ca_mg_l_per_day !== null) {
    rows.push({
      term: usage.ca_estimated ? t('dashboard.khUsage.caEstimated') : t('dashboard.khUsage.ca'),
      value: `${usage.ca_estimated ? '≈ ' : ''}${n(usage.supply_ca_mg_l_per_day, 1)} mg/l/${t('dashboard.khUsage.day')}`,
    });
  }
  // Say which volume the calculation used: the card showed 1.450 l, the aquarium 1800 L (audit §3.7).
  const volumeTerm =
    usage.volume_source === 'net_volume' ? 'dashboard.khUsage.volumeNet'
      : usage.volume_source === 'gross_volume' ? 'dashboard.khUsage.volumeGross'
        : 'dashboard.khUsage.volume';
  rows.push({ term: t(volumeTerm), value: `${n(usage.volume_liters, 0)} l` });

  // Calcium reactor: show how the KH added is computed (net of the tank water it is fed with).
  const calculation =
    usage.method === 'calcium_reactor' &&
    usage.effluent_kh_dkh != null &&
    usage.tank_kh_reference_dkh != null &&
    usage.effluent_liters_per_day != null
      ? t('dashboard.khUsage.reactorCalculation', {
        effluent: n(usage.effluent_kh_dkh, 1),
        tank: n(usage.tank_kh_reference_dkh, 1),
        liters: n(usage.effluent_liters_per_day, 2),
        volume: n(usage.volume_liters, 0),
        result: n(usage.supply_dkh_per_day),
      })
      : null;

  return (
    <>
      <div className="kh-usage-card__headline" data-testid="kh-usage-value">
        <span className="kh-usage-card__value">{n(usage.usage_dkh_per_day)}</span>
        <span className="kh-usage-card__unit">
          {t('dashboard.khUsage.unitPerDay', { week: n(usage.usage_dkh_per_day * 7, 1) })}
        </span>
      </div>
      <Text variant="muted" size="sm">
        {usage.kh_trend_dkh_per_day === null
          ? t('dashboard.khUsage.basisSupply')
          : t('dashboard.khUsage.basisTrend', { count: usage.kh_trend_measurements })}
      </Text>
      <dl className="kh-usage-card__rows">
        {rows.map((row) => (
          <div key={row.term} className="kh-usage-card__row">
            <dt className="kh-usage-card__term">{row.term}</dt>
            <dd className="kh-usage-card__definition">{row.value}</dd>
          </div>
        ))}
      </dl>
      {calculation && (
        <Text variant="muted" size="sm" data-testid="kh-usage-calculation">
          {calculation}
        </Text>
      )}
      {(usage.tank_kh_assumed || usage.tank_ca_assumed) && (
        <Text variant="muted" size="sm">{t('dashboard.khUsage.assumed')}</Text>
      )}
    </>
  );
}

export default KhUsageCard;
